import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Church, Loader2, Plus, Trash2, Pencil, X, Copy, Check, Rss, Image as ImageIcon, UploadCloud, Code2, GripVertical, Tv } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", feed_url: "", logo_url: "", accent_color: "#3B82F6", playlist_id: "", owner_id: "", poster_url: "" };

function CopyField({ value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testid}
      className="group flex w-full items-center gap-2 rounded-md border border-gray-800 bg-black/30 px-2.5 py-1.5 text-left transition-colors hover:border-amber-500/40"
    >
      <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-gray-300">{value}</span>
      {copied ? <Check className="h-3.5 w-3.5 shrink-0 text-emerald-400" /> : <Copy className="h-3.5 w-3.5 shrink-0 text-gray-500 group-hover:text-amber-400" />}
    </button>
  );
}

export default function ChurchBranding() {
  const [rows, setRows] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [posterUploading, setPosterUploading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const fileRef = useRef(null);
  const posterRef = useRef(null);

  const onDrop = async (toIdx) => {
    const from = dragIdx;
    setDragIdx(null);
    setOverIdx(null);
    if (from === null || from === toIdx) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(toIdx, 0, moved);
    setRows(next);
    try { await api.post("/admin/church-apps/reorder", { order: next.map((r) => r.id) }); toast.success("Channel order saved"); }
    catch { toast.error("Couldn't save order"); load(); }
  };

  const load = async () => {
    try { setRows((await api.get("/admin/church-apps")).data); }
    catch { toast.error("Failed to load church apps"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/playlists").then((r) => setPlaylists(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setAccounts(r.data.filter((u) => u.role !== "admin"))).catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, feed_url: c.feed_url, logo_url: c.logo_url, accent_color: c.accent_color || "#3B82F6", playlist_id: c.playlist_id || "", owner_id: c.owner_id || "", poster_url: c.poster_url || "" }); setOpen(true); };

  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/church-apps/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: res.data.logo_url }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onPosterPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setPosterUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/church-apps/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, poster_url: res.data.logo_url }));
      toast.success("Series poster uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Poster upload failed");
    } finally {
      setPosterUploading(false);
      if (posterRef.current) posterRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Church name is required"); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/admin/church-apps/${editing.id}`, form); toast.success("Church updated"); }
      else { await api.post("/admin/church-apps", form); toast.success("Church added"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    setBusy(c.id);
    try { await api.delete(`/admin/church-apps/${c.id}`); setRows((x) => x.filter((y) => y.id !== c.id)); toast.success("Church removed"); }
    catch { toast.error("Delete failed"); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-40"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-[1100px] mx-auto" data-testid="church-branding-page">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2.5">
            <Church className="h-7 w-7 text-amber-400" /> Church Branding
          </h1>
          <p className="text-gray-400 mt-1.5 text-sm max-w-2xl">
            Manage branding for each client church app — set a name, logo and feed URL. Outside apps fetch these
            details using the church's unique code via the public API.
          </p>
        </div>
        <button
          onClick={openNew}
          data-testid="add-church-btn"
          className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-amber-400"
        >
          <Plus className="h-4 w-4" /> Add Church
        </button>
      </div>

      <div className="mb-6 rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-4" data-testid="roku-global-feed">
        <p className="mb-2 flex items-center gap-2 text-sm font-medium text-white">
          <Tv className="h-4 w-4 text-amber-400" /> Roku Direct Publisher feed
        </p>
        <p className="mb-2.5 text-xs text-gray-400">Paste this URL into the Roku Direct Publisher console. It includes every church as its own series (seasons by year, one episode per service).</p>
        <CopyField value={`${API_BASE}/roku/feed.json`} testid="roku-global-feed-url" />
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-800 bg-[#0E131F] py-20 text-center" data-testid="church-empty-state">
          <Church className="mx-auto h-10 w-10 text-gray-600" />
          <p className="mt-3 text-gray-400">No church apps yet.</p>
          <button onClick={openNew} className="mt-4 inline-flex items-center gap-2 rounded-lg border border-amber-500/40 px-4 py-2 text-sm font-medium text-amber-300 hover:bg-amber-500/10">
            <Plus className="h-4 w-4" /> Add your first church
          </button>
        </div>
      ) : (
        <>
          <p className="mb-3 flex items-center gap-1.5 text-xs text-gray-500" data-testid="church-reorder-hint">
            <GripVertical className="h-3.5 w-3.5" /> Drag channels to set the order your TV app shows them.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
          {rows.map((c, i) => (
            <motion.div
              key={c.id}
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => { e.preventDefault(); if (overIdx !== i) setOverIdx(i); }}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              onDrop={() => onDrop(i)}
              data-testid={`church-card-${c.id}`}
              className={"rounded-xl border bg-[#0E131F] p-5 transition-colors " + (overIdx === i && dragIdx !== null && dragIdx !== i ? "border-amber-500/70 ring-1 ring-amber-500/40" : "border-gray-800") + (dragIdx === i ? " opacity-50" : "")}
            >
              <div className="flex items-start gap-3">
                <span data-testid={`church-drag-${c.id}`} className="mt-1 cursor-grab active:cursor-grabbing text-gray-600 hover:text-amber-400" title="Drag to reorder">
                  <GripVertical className="h-4 w-4" />
                </span>
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-gray-800 bg-black/40 flex items-center justify-center">
                  {c.logo_url
                    ? <img src={c.logo_url} alt={c.name} className="h-full w-full object-contain" />
                    : <ImageIcon className="h-5 w-5 text-gray-600" />}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-semibold text-white flex items-center gap-2" data-testid={`church-name-${c.id}`}>
                    <span className="inline-block h-3 w-3 shrink-0 rounded-full border border-white/20" style={{ backgroundColor: c.accent_color || "#3B82F6" }} data-testid={`church-accent-swatch-${c.id}`} />
                    <span className="truncate">{c.name}</span>
                  </h3>
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500">
                    <Rss className="h-3 w-3" />
                    <span className="truncate">{c.feed_url || "No feed URL"}</span>
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button onClick={() => openEdit(c)} data-testid={`edit-church-${c.id}`} className="rounded-md p-1.5 text-gray-400 hover:bg-gray-800 hover:text-white" title="Edit">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(c)} disabled={busy === c.id} data-testid={`delete-church-${c.id}`} className="rounded-md p-1.5 text-gray-400 hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50" title="Delete">
                    {busy === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="mt-4 space-y-2.5">
                <div>
                  <p className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                    <Code2 className="h-3 w-3" /> Unique code
                  </p>
                  <div className="inline-flex items-center rounded-md border border-amber-500/30 bg-amber-500/[0.06] px-2.5 py-1 font-mono text-sm font-semibold tracking-wider text-amber-300" data-testid={`church-code-${c.id}`}>
                    {c.code}
                  </div>
                </div>
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-500">Public API URL</p>
                  <CopyField value={`${API_BASE}/public/church-app/${c.code}`} testid={`church-apiurl-${c.id}`} />
                </div>
                <div>
                  <p className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-gray-500"><Tv className="h-3 w-3" /> Roku feed (this church)</p>
                  <CopyField value={`${API_BASE}/roku/feed.json?church=${c.code}`} testid={`church-roku-feed-${c.id}`} />
                </div>
                {c.playlist_id && (
                  <div>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-500">Embed shortcode link</p>
                    <CopyField value={`${window.location.origin}/embed/church/${c.code}`} testid={`church-embed-${c.id}`} />
                  </div>
                )}
              </div>
            </motion.div>
          ))}
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0E131F] border-gray-800 text-white sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit Church" : "Add Church"}</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">
              Set the church name, logo and feed URL. An outside app fetches these using the church's unique code.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div>
              <Label className="text-gray-300">Church Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Pentecostal Assembly Church"
                data-testid="church-name-input"
                className="mt-1.5 bg-black/30 border-gray-800"
              />
            </div>

            <div>
              <Label className="text-gray-300">Feed URL</Label>
              <Input
                value={form.feed_url}
                onChange={(e) => setForm({ ...form, feed_url: e.target.value })}
                placeholder="https://…/feed.m3u8 or feed.json"
                data-testid="church-feed-input"
                className="mt-1.5 bg-black/30 border-gray-800"
              />
            </div>

            <div>
              <Label className="text-gray-300">Logo</Label>
              <div className="mt-1.5 flex items-center gap-4">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-gray-800 bg-black/40 flex items-center justify-center">
                  {form.logo_url
                    ? <img src={form.logo_url} alt="Logo preview" className="h-full w-full object-contain" data-testid="church-logo-preview" />
                    : <ImageIcon className="h-5 w-5 text-gray-600" />}
                </div>
                <div className="flex-1">
                  <input ref={fileRef} type="file" accept="image/*" onChange={onLogoPick} className="hidden" data-testid="church-logo-file" />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="church-logo-upload-btn"
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-200 hover:border-amber-500/40 hover:text-white disabled:opacity-50"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                    {uploading ? "Uploading…" : (form.logo_url ? "Replace logo" : "Upload logo")}
                  </button>
                  <p className="mt-1.5 text-[11px] text-gray-500">PNG, JPG, WEBP or SVG — up to 5 MB.</p>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-gray-300">Series poster (portrait)</Label>
              <div className="mt-1.5 flex items-center gap-4">
                <div className="h-24 w-16 shrink-0 overflow-hidden rounded-lg border border-gray-800 bg-black/40 flex items-center justify-center">
                  {form.poster_url
                    ? <img src={form.poster_url} alt="Poster preview" className="h-full w-full object-cover" data-testid="church-poster-preview" />
                    : <Tv className="h-5 w-5 text-gray-600" />}
                </div>
                <div className="flex-1">
                  <input ref={posterRef} type="file" accept="image/*" onChange={onPosterPick} className="hidden" data-testid="church-poster-file" />
                  <button
                    type="button"
                    onClick={() => posterRef.current?.click()}
                    disabled={posterUploading}
                    data-testid="church-poster-upload-btn"
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-200 hover:border-amber-500/40 hover:text-white disabled:opacity-50"
                  >
                    {posterUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                    {posterUploading ? "Uploading…" : (form.poster_url ? "Replace poster" : "Upload poster")}
                  </button>
                  <p className="mt-1.5 text-[11px] text-gray-500">Tall 2:3 image (e.g. 600×900) for the Roku series tile.</p>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-gray-300">Accent color</Label>
              <div className="mt-1.5 flex items-center gap-3">
                <input
                  type="color"
                  value={form.accent_color}
                  onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                  data-testid="church-accent-color"
                  className="h-10 w-14 cursor-pointer rounded-md border border-gray-800 bg-black/30 p-1"
                />
                <Input
                  value={form.accent_color}
                  onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                  placeholder="#3B82F6"
                  data-testid="church-accent-hex"
                  className="bg-black/30 border-gray-800 font-mono w-32"
                />
                <span className="text-[11px] text-gray-500">Client apps theme their UI with this color.</span>
              </div>
            </div>

            <div>
              <Label className="text-gray-300">Account</Label>
              <select
                value={form.owner_id}
                onChange={(e) => setForm({ ...form, owner_id: e.target.value, playlist_id: "" })}
                data-testid="church-account-select"
                className="mt-1.5 w-full rounded-md border border-gray-800 bg-black/30 px-3 py-2 text-sm text-gray-100 focus:border-amber-500/40 focus:outline-none"
              >
                <option value="">— Select the client account —</option>
                {accounts.map((a) => (
                  <option key={a.user_id} value={a.user_id}>{a.organization || a.name || a.email}{a.email ? ` — ${a.email}` : ""}</option>
                ))}
              </select>
              <p className="mt-1.5 text-[11px] text-gray-500">This church is linked to this account. Its videos auto-fill the channel.</p>
            </div>

            <div>
              <Label className="text-gray-300">Linked playlist</Label>
              <select
                value={form.playlist_id}
                onChange={(e) => setForm({ ...form, playlist_id: e.target.value })}
                data-testid="church-playlist-select"
                disabled={!form.owner_id}
                className="mt-1.5 w-full rounded-md border border-gray-800 bg-black/30 px-3 py-2 text-sm text-gray-100 focus:border-amber-500/40 focus:outline-none disabled:opacity-50"
              >
                <option value="">All Videos (auto-linked — never empty)</option>
                {playlists.filter((p) => p.owner_id === form.owner_id && p.name !== "All Videos").map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.item_count != null ? ` (${p.item_count})` : ""}</option>
                ))}
              </select>
              <p className="mt-1.5 text-[11px] text-gray-500">Leave on "All Videos" so the channel always has content, or pick a specific playlist.</p>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button
              onClick={save}
              disabled={saving || uploading}
              data-testid="save-church-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-black hover:bg-amber-400 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editing ? "Save changes" : "Add Church"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
